/*
 * HMI script language (docs/hmi-panel-ausbau.md §5).
 *
 * Small on purpose: buttons ("+" and "-" on a setpoint), rules ("red when
 * the room is 2 K above setpoint") and pipe animations ("flow while the pump
 * runs") need arithmetic, comparisons, a handful of functions and writes.
 * It is parsed and interpreted here, never handed to eval or Function: a
 * project body can come from a cached file on a gateway, and nothing in it
 * may reach the page's JavaScript.
 *
 *   schreibe(soll, begrenze(runde(wert(soll) + 0.5, 0.5), 15, 28))
 *   wert(ist) > wert(soll) + 2
 *   x = wert(soll); schreibe(soll, x - 1)
 *
 * Names: a name from the widget's `points` stands for that data point. Used
 * bare it reads the value (`soll + 1`); `wert(soll)` says the same thing more
 * plainly. `schreibe` needs the name itself. Any other name assigned with `=`
 * is a local variable for the rest of the script.
 *
 * Execution does not write. It returns the writes and the screen change it
 * asked for; the caller performs them. That keeps a script atomic (a runtime
 * error writes nothing) and makes it testable without a data source.
 *
 * Pure module, no runtime imports.
 */

export class HmiScriptError extends Error {
  readonly position: number;
  constructor(message: string, position: number) {
    super(message);
    this.name = 'HmiScriptError';
    this.position = position;
  }
}

export const MAX_STEPS = 1000;
export const MAX_WRITES = 8;

// ---------------------------------------------------------------- tokens

type TokType = 'num' | 'str' | 'name' | 'op' | 'end';
interface Tok { type: TokType; value: string; pos: number; }

const OPS = ['&&', '||', '==', '!=', '<=', '>=', '+', '-', '*', '/', '%', '<', '>', '!', '?', ':', '(', ')', ',', ';', '='];

function tokenize(src: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i]!;
    if (/\s/.test(ch)) { i++; continue; }
    if (ch === '/' && src[i + 1] === '/') {
      while (i < src.length && src[i] !== '\n') i++;
      continue;
    }
    if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(src[i + 1] ?? ''))) {
      const start = i;
      while (i < src.length && /[0-9.]/.test(src[i]!)) i++;
      const text = src.slice(start, i);
      if (!/^\d*\.?\d+$|^\d+\.$/.test(text)) throw new HmiScriptError(`Unlesbare Zahl '${text}'`, start);
      out.push({ type: 'num', value: text, pos: start });
      continue;
    }
    if (ch === '"' || ch === "'") {
      const start = i++;
      let s = '';
      while (i < src.length && src[i] !== ch) {
        if (src[i] === '\\' && i + 1 < src.length) { s += src[i + 1]; i += 2; continue; }
        s += src[i++];
      }
      if (i >= src.length) throw new HmiScriptError('Text ohne schließendes Anführungszeichen', start);
      i++;
      out.push({ type: 'str', value: s, pos: start });
      continue;
    }
    if (/[A-Za-z_äöüÄÖÜß]/.test(ch)) {
      const start = i;
      while (i < src.length && /[A-Za-z0-9_äöüÄÖÜß]/.test(src[i]!)) i++;
      out.push({ type: 'name', value: src.slice(start, i), pos: start });
      continue;
    }
    const op = OPS.find((o) => src.startsWith(o, i));
    if (!op) throw new HmiScriptError(`Unerwartetes Zeichen '${ch}'`, i);
    out.push({ type: 'op', value: op, pos: i });
    i += op.length;
  }
  out.push({ type: 'end', value: '', pos: src.length });
  return out;
}

// ---------------------------------------------------------------- AST

export type Node =
  | { t: 'lit'; v: unknown; pos: number }
  | { t: 'name'; name: string; pos: number }
  | { t: 'unary'; op: '-' | '!'; arg: Node; pos: number }
  | { t: 'bin'; op: string; l: Node; r: Node; pos: number }
  | { t: 'cond'; test: Node; yes: Node; no: Node; pos: number }
  | { t: 'call'; fn: string; args: Node[]; pos: number }
  | { t: 'assign'; name: string; value: Node; pos: number };

export interface Program { statements: Node[]; source: string; }

class Parser {
  private i = 0;
  private readonly toks: Tok[];
  constructor(toks: Tok[]) { this.toks = toks; }

  private peek(): Tok { return this.toks[this.i]!; }
  private next(): Tok { return this.toks[this.i++]!; }
  private isOp(v: string): boolean { const t = this.peek(); return t.type === 'op' && t.value === v; }
  private expectOp(v: string): void {
    const t = this.next();
    if (t.type !== 'op' || t.value !== v) {
      throw new HmiScriptError(`Erwartet '${v}', gefunden '${t.value || 'Ende'}'`, t.pos);
    }
  }

  program(): Node[] {
    const out: Node[] = [];
    while (this.peek().type !== 'end') {
      if (this.isOp(';')) { this.next(); continue; }
      out.push(this.statement());
      if (!this.isOp(';') && this.peek().type !== 'end') {
        const t = this.peek();
        throw new HmiScriptError(`Erwartet ';' oder Ende, gefunden '${t.value}'`, t.pos);
      }
    }
    return out;
  }

  private statement(): Node {
    const t = this.peek();
    const after = this.toks[this.i + 1];
    if (t.type === 'name' && after?.type === 'op' && after.value === '=') {
      this.next(); this.next();
      return { t: 'assign', name: t.value, value: this.expr(), pos: t.pos };
    }
    return this.expr();
  }

  expr(): Node { return this.ternary(); }

  private ternary(): Node {
    const test = this.or();
    if (!this.isOp('?')) return test;
    const pos = this.next().pos;
    const yes = this.ternary();
    this.expectOp(':');
    const no = this.ternary();
    return { t: 'cond', test, yes, no, pos };
  }

  private binary(next: () => Node, ops: string[]): Node {
    let l = next();
    while (this.peek().type === 'op' && ops.includes(this.peek().value)) {
      const op = this.next();
      l = { t: 'bin', op: op.value, l, r: next(), pos: op.pos };
    }
    return l;
  }

  private or(): Node { return this.binary(() => this.and(), ['||']); }
  private and(): Node { return this.binary(() => this.eq(), ['&&']); }
  private eq(): Node { return this.binary(() => this.cmp(), ['==', '!=']); }
  private cmp(): Node { return this.binary(() => this.add(), ['<', '<=', '>', '>=']); }
  private add(): Node { return this.binary(() => this.mul(), ['+', '-']); }
  private mul(): Node { return this.binary(() => this.unary(), ['*', '/', '%']); }

  private unary(): Node {
    if (this.isOp('-') || this.isOp('!')) {
      const op = this.next();
      return { t: 'unary', op: op.value as '-' | '!', arg: this.unary(), pos: op.pos };
    }
    return this.primary();
  }

  private primary(): Node {
    const t = this.next();
    if (t.type === 'num') return { t: 'lit', v: Number(t.value), pos: t.pos };
    if (t.type === 'str') return { t: 'lit', v: t.value, pos: t.pos };
    if (t.type === 'name') {
      if (t.value === 'true' || t.value === 'wahr') return { t: 'lit', v: true, pos: t.pos };
      if (t.value === 'false' || t.value === 'falsch') return { t: 'lit', v: false, pos: t.pos };
      if (t.value === 'null') return { t: 'lit', v: null, pos: t.pos };
      if (this.isOp('(')) {
        this.next();
        const args: Node[] = [];
        if (!this.isOp(')')) {
          args.push(this.expr());
          while (this.isOp(',')) { this.next(); args.push(this.expr()); }
        }
        this.expectOp(')');
        return { t: 'call', fn: t.value, args, pos: t.pos };
      }
      return { t: 'name', name: t.value, pos: t.pos };
    }
    if (t.type === 'op' && t.value === '(') {
      const e = this.expr();
      this.expectOp(')');
      return e;
    }
    throw new HmiScriptError(t.type === 'end' ? 'Ausdruck bricht ab' : `Unerwartet '${t.value}'`, t.pos);
  }
}

const cache = new Map<string, Program>();

/** Parse once; the same text is parsed again only after a cache miss. */
export function compile(source: string): Program {
  const hit = cache.get(source);
  if (hit) return hit;
  const statements = new Parser(tokenize(source)).program();
  const program = { statements, source };
  if (cache.size > 500) cache.clear();
  cache.set(source, program);
  return program;
}

/** Syntax check for the editor: null when fine, otherwise the error. */
export function check(source: string): HmiScriptError | null {
  try { compile(source); return null; } catch (e) {
    return e instanceof HmiScriptError ? e : new HmiScriptError(String(e), 0);
  }
}

// ---------------------------------------------------------------- run

export interface ScriptContext {
  /** Names of the widget's points. */
  pointNames: ReadonlySet<string>;
  /** Current value of a named point; undefined when not known yet. */
  value(name: string): unknown;
}

export interface ScriptWrite { point: string; value: unknown; }

export interface ScriptOutcome {
  result: unknown;
  writes: ScriptWrite[];
  navigate: string | null;
}

const ALIASES: Record<string, string> = {
  wert: 'value', schreibe: 'write', begrenze: 'clamp', runde: 'round', wenn: 'if',
  gehe: 'navigate', zahl: 'number',
};

function num(v: unknown, pos: number, what: string): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  throw new HmiScriptError(`${what}: keine Zahl (${v === undefined ? 'noch kein Wert' : JSON.stringify(v)})`, pos);
}

function truthy(v: unknown): boolean {
  if (typeof v === 'number') return v !== 0 && !Number.isNaN(v);
  if (typeof v === 'string') return v.length > 0;
  return Boolean(v);
}

function looseEquals(a: unknown, b: unknown): boolean {
  if (typeof a === 'number' || typeof b === 'number') {
    const na = typeof a === 'boolean' ? (a ? 1 : 0) : Number(a);
    const nb = typeof b === 'boolean' ? (b ? 1 : 0) : Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return Math.abs(na - nb) < 1e-9;
  }
  return a === b;
}

/**
 * Run a compiled script. Throws HmiScriptError on any runtime error; then
 * nothing was written, because writes only leave through the outcome.
 */
export function run(program: Program, ctx: ScriptContext): ScriptOutcome {
  let steps = 0;
  const locals = new Map<string, unknown>();
  const writes: ScriptWrite[] = [];
  let navigate: string | null = null;

  const pointArg = (n: Node, fn: string): string => {
    if (n.t !== 'name' || !ctx.pointNames.has(n.name)) {
      throw new HmiScriptError(`${fn}: erwartet den Namen eines Datenpunkts`, n.pos);
    }
    return n.name;
  };

  const ev = (n: Node): unknown => {
    if (++steps > MAX_STEPS) throw new HmiScriptError(`Skript zu lang (mehr als ${MAX_STEPS} Schritte)`, n.pos);
    switch (n.t) {
      case 'lit': return n.v;
      case 'name':
        if (locals.has(n.name)) return locals.get(n.name);
        if (ctx.pointNames.has(n.name)) return ctx.value(n.name);
        throw new HmiScriptError(`Unbekannter Name '${n.name}'`, n.pos);
      case 'assign':
        if (ctx.pointNames.has(n.name)) {
          throw new HmiScriptError(`'${n.name}' ist ein Datenpunkt; schreiben mit schreibe(${n.name}, …)`, n.pos);
        }
        locals.set(n.name, ev(n.value));
        return locals.get(n.name);
      case 'unary': {
        const v = ev(n.arg);
        return n.op === '-' ? -num(v, n.pos, 'Minus') : !truthy(v);
      }
      case 'cond': return truthy(ev(n.test)) ? ev(n.yes) : ev(n.no);
      case 'bin': {
        if (n.op === '&&') { const l = ev(n.l); return truthy(l) ? ev(n.r) : l; }
        if (n.op === '||') { const l = ev(n.l); return truthy(l) ? l : ev(n.r); }
        const l = ev(n.l);
        const r = ev(n.r);
        switch (n.op) {
          case '==': return looseEquals(l, r);
          case '!=': return !looseEquals(l, r);
          case '+':
            if (typeof l === 'string' || typeof r === 'string') return `${l ?? ''}${r ?? ''}`;
            return num(l, n.pos, '+') + num(r, n.pos, '+');
          case '-': return num(l, n.pos, '-') - num(r, n.pos, '-');
          case '*': return num(l, n.pos, '*') * num(r, n.pos, '*');
          case '/': {
            const d = num(r, n.pos, '/');
            if (d === 0) throw new HmiScriptError('Division durch 0', n.pos);
            return num(l, n.pos, '/') / d;
          }
          case '%': {
            const d = num(r, n.pos, '%');
            if (d === 0) throw new HmiScriptError('Division durch 0', n.pos);
            return num(l, n.pos, '%') % d;
          }
          case '<': return num(l, n.pos, '<') < num(r, n.pos, '<');
          case '<=': return num(l, n.pos, '<=') <= num(r, n.pos, '<=');
          case '>': return num(l, n.pos, '>') > num(r, n.pos, '>');
          case '>=': return num(l, n.pos, '>=') >= num(r, n.pos, '>=');
        }
        throw new HmiScriptError(`Unbekannter Operator '${n.op}'`, n.pos);
      }
      case 'call': {
        const fn = ALIASES[n.fn] ?? n.fn;
        const a = n.args;
        const arity = (k: number) => {
          if (a.length !== k) throw new HmiScriptError(`${n.fn} erwartet ${k} Angabe(n), bekommt ${a.length}`, n.pos);
        };
        switch (fn) {
          case 'value': arity(1); return ctx.value(pointArg(a[0]!, n.fn));
          case 'write': {
            arity(2);
            const point = pointArg(a[0]!, n.fn);
            const v = ev(a[1]!);
            if (writes.length >= MAX_WRITES) throw new HmiScriptError(`Mehr als ${MAX_WRITES} Schreibvorgänge`, n.pos);
            writes.push({ point, value: v });
            return v;
          }
          case 'clamp': {
            arity(3);
            const v = num(ev(a[0]!), n.pos, n.fn);
            const lo = num(ev(a[1]!), n.pos, n.fn);
            const hi = num(ev(a[2]!), n.pos, n.fn);
            return Math.min(Math.max(v, lo), hi);
          }
          case 'round': {
            if (a.length < 1 || a.length > 2) throw new HmiScriptError(`${n.fn} erwartet 1 oder 2 Angaben`, n.pos);
            const v = num(ev(a[0]!), n.pos, n.fn);
            const step = a.length === 2 ? num(ev(a[1]!), n.pos, n.fn) : 1;
            if (step <= 0) throw new HmiScriptError(`${n.fn}: Schrittweite muss größer 0 sein`, n.pos);
            // Round on the step, then strip binary noise (0.1 + 0.2).
            return Number((Math.round(v / step) * step).toFixed(10));
          }
          case 'min': case 'max': {
            if (a.length === 0) throw new HmiScriptError(`${n.fn} braucht Angaben`, n.pos);
            const vs = a.map((x) => num(ev(x), n.pos, n.fn));
            return fn === 'min' ? Math.min(...vs) : Math.max(...vs);
          }
          case 'abs': arity(1); return Math.abs(num(ev(a[0]!), n.pos, n.fn));
          case 'number': arity(1); return num(ev(a[0]!), n.pos, n.fn);
          case 'if': arity(3); return truthy(ev(a[0]!)) ? ev(a[1]!) : ev(a[2]!);
          case 'navigate': {
            arity(1);
            const id = ev(a[0]!);
            if (typeof id !== 'string' || id === '') throw new HmiScriptError(`${n.fn}: erwartet eine Bildschirm-Id`, n.pos);
            navigate = id;
            return id;
          }
        }
        throw new HmiScriptError(`Unbekannte Funktion '${n.fn}'`, n.pos);
      }
    }
  };

  let result: unknown = null;
  for (const s of program.statements) result = ev(s);
  return { result, writes, navigate };
}

/** Evaluate an expression for a rule or animation: true/false, and false
 *  when the script fails (a value not there yet must not light up red). */
export function evaluateCondition(source: string | undefined, ctx: ScriptContext): boolean {
  if (!source || source.trim() === '') return false;
  try {
    const out = run(compile(source), ctx);
    return out.writes.length === 0 && out.navigate === null && truthy(out.result);
  } catch {
    return false;
  }
}
